import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { useAuthStore } from "../../auth/store";
import { AppDataGrid, type AppColumn } from "../../components/AppDataGrid";
import { FormTitle } from "../../components/FormTitle";
import { fmtLocalDateTime, nowClientWire } from "../../utils/datetime";
import { useConfirm, showDialog } from "../../components/confirm";
import { useNotify } from "../../components/notifications";
import { TopbarStepper } from "../../components/StepIsland/TopbarStepper";
import { type IslandStep } from "../../components/StepIsland/StepIsland";
import { StepHint } from "../../components/StepHint/StepHint";
import { NotesListPopup, useNotesCount } from "./SdmsProjectNotes";
import "../backlog/backlog.css";
// Reuse the SDMS detail's tab styling (dv-tabs / dv-tab / dv-active) so the
// Sample Results / QC Results tabs look exactly like SDMS Result Validation.
import "./SdmsPackageDetail.css";

/**
 * Result Validation / Approval DETAIL view — the drill-in from the manual Result
 * Validation OR Result Approval list (ALPACA's ResultEntry_Validation /
 * ResultEntry_Approval DashboardView).  ONE component serves BOTH Data-Review
 * stages via the batch's `mode` prop; the only differences are the endpoint base,
 * the editable stamp columns, and the terminal action verb:
 *   • mode 'validation' (default) — Level-2; ValidatedBy/ValidatedDate editable;
 *     action = Validate (Status 1→2 PendingApproval).
 *   • mode 'approval'             — Level-3; ApprovedBy/ApprovedDate editable while
 *     ValidatedBy/ValidatedDate are read-only (prior stage); action = Approve
 *     (Status 2→3 PendingReporting).
 *
 * Per-sample results for one QC batch / job, split into Sample Results / QC
 * Results tabs, with Project Notes · Rollback · Validate|Approve (reusing the
 * list-view endpoints).  The editable stamp columns mirror ALPACA's inline
 * Employee-lookup + date-picker cells; ticking a row previews the stamp and the
 * DB write happens on submit.  First two columns (+ checkbox) are FROZEN.  Built
 * on the shared formKit components per the BO UI Standard.
 */

interface SampleRow {
  id: string;
  qcBatchID: string | null;
  uqabid: string | null; // batch Oid — job mode collects these to Validate/Rollback
  sys: string | null;
  sampleName: string | null;
  groupTest: string | null;
  test: string | null;
  method: string | null;
  parameter: string | null;
  numericResult: string | null;
  result: string | null;
  units: string | null;
  rl: string | null;
  mdl: string | null;
  loq: string | null;
  spikeAmount: string | null;
  spikeAmountUnit: string | null;
  rec: string | null;
  rpd: string | null;
  analyzedDate: string | null;
  analyzedBy: string | null;
  validatedDate: string | null;
  validatedBy: string | null;
  approvedDate: string | null; // approval mode only
  approvedBy: string | null; // approval mode only
  matrix: string | null;
  component: string | null;
  statusText: string | null;
  statusId: string | null;
  isQc: boolean;
  qcType: string | null;
  statusCode: number;
}

export interface ResultValidationBatch {
  /** Batch Oid (scope 'batch') OR job/Samplecheckin Oid (scope 'job'). */
  id: string;
  qcBatchID: string | null;
  client: string | null;
  projectID: string | null;
  /** Project name — shown in the header identity line. */
  projectName?: string | null;
  /** Job ID label — shown in the header identity line. */
  jobID?: string | null;
  /** Samplecheckin Oid for the row's JOB.  In QCBatchID mode the Sample tab is
   *  scoped to this job (QC stays batch-wide); passed to validate/rollback so
   *  only this job's samples flip.  Present in both modes. */
  jobOid?: string | null;
  /** 'batch' (QCBatchID mode, default) = ONE job's samples in one batch + that
   *  batch's QC (per-(batch,job)); 'job' (JobID mode) = the whole job across its
   *  batches — mirrors ALPACA's two "Search By" Retrieve targets. */
  scope?: "batch" | "job";
  /** 'validation' (default) = Level-2 Result Validation (Status 1→2, ValidatedBy/Date
   *  editable). 'approval' = Level-3 Result Approval (Status 2→3, ApprovedBy/Date
   *  editable, ValidatedBy/Date read-only). The SAME component serves both. */
  mode?: "validation" | "approval";
}

interface Props {
  batch: ResultValidationBatch;
  onClose: () => void;
  /** Called after a successful Validate / Rollback — parent refreshes the list and closes. */
  onDone: () => void;
  /** MAC write permission — when false Rollback / Validate / Approve are disabled
   *  and a read-only banner is shown. Defaults to true. */
  canWrite?: boolean;
}

// Endpoint base is mode-derived (see the component): manual Result Validation
// (Level-2) vs Result Approval (Level-3).  Both expose the same sub-routes
// (/{batchOid}/samples, /job/{jobOid}/samples, /{batchOid}/samples/save,
// /rollback, /rollback/precheck) plus their terminal action (/validate | /approve).
const ENDPOINT_VAL = "/api/v1/analysis/manual-result-validation";
const ENDPOINT_APPR = "/api/v1/analysis/result-approval";
// Employee roster for the ValidatedBy/ApprovedBy dropdown — ALPACA's Employee
// lookup: "N/A" first, then the full roster (incl. Administrator) sorted.
const EMP_OPTIONS_URL = "/api/v1/analysis/result-validation/validated-by-options";

// ALPACA parity: empty cells render BLANK, not a '—' placeholder.
function fmtDateTime(v?: string | null): string {
  return v ? fmtLocalDateTime(v) || "" : "";
}

// Column set mirrors ALPACA's ResultEntry_Validation grid.  All cells render
// BLANK when empty (ALPACA parity — no '—' placeholder).  The first two columns
// carry explicit numeric widths so the FROZEN-column offsets compute correctly.
const COLS: AppColumn<SampleRow>[] = [
  { field: "sys", header: "SysSampleCode", width: 150, text: (r) => r.sys ?? "", render: (r) => r.sys ?? "" },
  {
    field: "sampleName",
    header: "SampleName",
    width: 180,
    text: (r) => r.sampleName ?? "",
    render: (r) => r.sampleName ?? "",
  },
  { field: "groupTest", header: "GroupTest", text: (r) => r.groupTest ?? "", render: (r) => r.groupTest ?? "" },
  { field: "test", header: "Test", text: (r) => r.test ?? "", render: (r) => r.test ?? "" },
  // Method (MethodName/MethodNumber) is HIDDEN in ALPACA's Result Validation AND
  // Approval Sample Results ListViews (Model.xafml Index="-1", verified 2026-07-10),
  // and LOQ is not a column there at all — so neither is shown on the Sample tab.
  { field: "parameter", header: "Parameter", text: (r) => r.parameter ?? "", render: (r) => r.parameter ?? "" },
  {
    field: "numericResult",
    header: "NumericResult",
    text: (r) => r.numericResult ?? "",
    render: (r) => r.numericResult ?? "",
  },
  { field: "result", header: "Result", text: (r) => r.result ?? "", render: (r) => r.result ?? "" },
  { field: "units", header: "Units", text: (r) => r.units ?? "", render: (r) => r.units ?? "" },
  { field: "rl", header: "RL", text: (r) => r.rl ?? "", render: (r) => r.rl ?? "" },
  { field: "mdl", header: "MDL", text: (r) => r.mdl ?? "", render: (r) => r.mdl ?? "" },
  {
    field: "analyzedDate",
    header: "AnalyzedDate",
    text: (r) => fmtDateTime(r.analyzedDate),
    render: (r) => fmtDateTime(r.analyzedDate),
  },
  { field: "analyzedBy", header: "AnalyzedBy", text: (r) => r.analyzedBy ?? "", render: (r) => r.analyzedBy ?? "" },
  {
    field: "validatedDate",
    header: "ValidatedDate",
    text: (r) => fmtDateTime(r.validatedDate),
    render: (r) => fmtDateTime(r.validatedDate),
  },
  { field: "validatedBy", header: "ValidatedBy", text: (r) => r.validatedBy ?? "", render: (r) => r.validatedBy ?? "" },
  { field: "matrix", header: "Matrix", text: (r) => r.matrix ?? "", render: (r) => r.matrix ?? "" },
  // LIMS Sample Results order after ValidatedBy/ApprovedBy: Matrix · QCBatchID ·
  // Component · Status · StatusID (verified vs the ResultEntry_Approval grid,
  // 2026-07-10 — QCBatchID precedes Component).
  { field: "qcBatchID", header: "QCBatchID", text: (r) => r.qcBatchID ?? "", render: (r) => r.qcBatchID ?? "" },
  { field: "component", header: "Component", text: (r) => r.component ?? "", render: (r) => r.component ?? "" },
  { field: "statusText", header: "Status", text: (r) => r.statusText ?? "", render: (r) => r.statusText ?? "" },
  { field: "statusId", header: "StatusID", text: (r) => r.statusId ?? "", render: (r) => r.statusId ?? "" },
];

// QC Results tab columns.  LIMS orders this grid DIFFERENTLY per Data-Review stage
// (both verified in Model.xafml, 2026-07-10):
//   • Result Validation (SampleParameter_ListView_Copy_QCResultValidation) — this
//     base array's order: … SpikeAmountUnit · %Rec · %RPD · AnalyzedDate · AnalyzedBy
//     · ValidatedDate · ValidatedBy · Status · Matrix · Method (Method LAST).
//   • Result Approval  (SampleParameter_ListView_Copy_QCResultApproval) — Method
//     right after Test, and AnalyzedDate BETWEEN %Rec and %RPD:
//     … Test · Method · Parameter … SpikeAmountUnit · %Rec · AnalyzedDate · %RPD ·
//     AnalyzedBy · ValidatedDate · ValidatedBy · [ApprovedDate · ApprovedBy] · Status
//     · Matrix.
// QC_COLS holds the VALIDATION order; qcColsForMode() reorders those two columns for
// Approval.  Status renders the enum text on BOTH (the QC ListViews bind Status to the
// Samplestatus ENUM, not SampleStatusNew — the samples SP returns enum text for QC
// rows).  The stamp columns are inline-editable per mode (see withStampEditors).
// First two columns carry explicit widths for the frozen-column offsets.
const QC_COLS: AppColumn<SampleRow>[] = [
  {
    field: "qcBatchID",
    header: "QCBatchID",
    width: 150,
    text: (r) => r.qcBatchID ?? "",
    render: (r) => r.qcBatchID ?? "",
  },
  { field: "qcType", header: "QCType", width: 150, text: (r) => r.qcType ?? "", render: (r) => r.qcType ?? "" },
  { field: "sys", header: "SysSampleCode", text: (r) => r.sys ?? "", render: (r) => r.sys ?? "" },
  { field: "test", header: "Test", text: (r) => r.test ?? "", render: (r) => r.test ?? "" },
  { field: "parameter", header: "Parameter", text: (r) => r.parameter ?? "", render: (r) => r.parameter ?? "" },
  {
    field: "numericResult",
    header: "NumericResult",
    text: (r) => r.numericResult ?? "",
    render: (r) => r.numericResult ?? "",
  },
  { field: "result", header: "Result", text: (r) => r.result ?? "", render: (r) => r.result ?? "" },
  { field: "units", header: "Units", text: (r) => r.units ?? "", render: (r) => r.units ?? "" },
  { field: "rl", header: "RL", text: (r) => r.rl ?? "", render: (r) => r.rl ?? "" },
  { field: "mdl", header: "MDL", text: (r) => r.mdl ?? "", render: (r) => r.mdl ?? "" },
  // SpikeAmount is numeric → RIGHT-aligned, matching ALPACA's QC grid (user request 2026-07-10).
  {
    field: "spikeAmount",
    header: "SpikeAmount",
    align: "right",
    text: (r) => r.spikeAmount ?? "",
    render: (r) => r.spikeAmount ?? "",
  },
  {
    field: "spikeAmountUnit",
    header: "SpikeAmountUnit",
    text: (r) => r.spikeAmountUnit ?? "",
    render: (r) => r.spikeAmountUnit ?? "",
  },
  { field: "rec", header: "%Rec", text: (r) => r.rec ?? "", render: (r) => r.rec ?? "" },
  { field: "rpd", header: "%RPD", text: (r) => r.rpd ?? "", render: (r) => r.rpd ?? "" },
  {
    field: "analyzedDate",
    header: "AnalyzedDate",
    text: (r) => fmtDateTime(r.analyzedDate),
    render: (r) => fmtDateTime(r.analyzedDate),
  },
  { field: "analyzedBy", header: "AnalyzedBy", text: (r) => r.analyzedBy ?? "", render: (r) => r.analyzedBy ?? "" },
  {
    field: "validatedDate",
    header: "ValidatedDate",
    text: (r) => fmtDateTime(r.validatedDate),
    render: (r) => fmtDateTime(r.validatedDate),
  },
  { field: "validatedBy", header: "ValidatedBy", text: (r) => r.validatedBy ?? "", render: (r) => r.validatedBy ?? "" },
  { field: "statusText", header: "Status", text: (r) => r.statusText ?? "", render: (r) => r.statusText ?? "" },
  { field: "matrix", header: "Matrix", text: (r) => r.matrix ?? "", render: (r) => r.matrix ?? "" },
  { field: "method", header: "Method", text: (r) => r.method ?? "", render: (r) => r.method ?? "" },
];

// QC column order per Data-Review stage.  Validation uses QC_COLS as-is; Approval
// reorders two columns to match SampleParameter_ListView_Copy_QCResultApproval:
// Method moves to right after Test, and AnalyzedDate moves BETWEEN %Rec and %RPD.
// Reuses the SAME column objects (by field) — no duplicated render/text logic.
function qcColsForMode(mode: "validation" | "approval"): AppColumn<SampleRow>[] {
  if (mode !== "approval") return QC_COLS;
  const by = (f: keyof SampleRow) => QC_COLS.find((c) => c.field === f)!;
  return [
    by("qcBatchID"),
    by("qcType"),
    by("sys"),
    by("test"),
    by("method"),
    by("parameter"),
    by("numericResult"),
    by("result"),
    by("units"),
    by("rl"),
    by("mdl"),
    by("spikeAmount"),
    by("spikeAmountUnit"),
    by("rec"),
    by("analyzedDate"),
    by("rpd"),
    by("analyzedBy"),
    by("validatedDate"),
    by("validatedBy"),
    by("statusText"),
    by("matrix"),
  ];
}

// The two editable stamp columns (a 24-hour DATE+TIME editor + an Employee combo),
// mirroring ALPACA's inline ValidatedDate/ValidatedBy (Validation) and
// ApprovedDate/ApprovedBy (Approval) cells.  In VALIDATION mode ValidatedDate/By
// are editable.  In APPROVAL mode the ApprovedDate/By pair is INSERTED after
// ValidatedBy and is editable, while ValidatedDate/By become the prior-stage,
// read-only stamp.  ALPACA parity: DataReviewController's isVerifyView stamps
// ApprovedDate/ApprovedBy, else ValidatedDate/ValidatedBy.
const APPROVED_DATE_COL: AppColumn<SampleRow> = {
  field: "approvedDate",
  header: "ApprovedDate",
  text: (r) => fmtDateTime(r.approvedDate),
  render: (r) => fmtDateTime(r.approvedDate),
};
const APPROVED_BY_COL: AppColumn<SampleRow> = {
  field: "approvedBy",
  header: "ApprovedBy",
  text: (r) => r.approvedBy ?? "",
  render: (r) => r.approvedBy ?? "",
};

function withStampEditors(
  cols: AppColumn<SampleRow>[],
  empOptions: string[],
  mode: "validation" | "approval",
): AppColumn<SampleRow>[] {
  const combo = { type: "combo" as const, options: empOptions };
  const dt = { type: "datetime" as const };
  if (mode === "approval") {
    const out: AppColumn<SampleRow>[] = [];
    for (const c of cols) {
      out.push(c); // ValidatedDate / ValidatedBy stay read-only (prior stage).
      if (c.field === "validatedBy") {
        out.push({ ...APPROVED_DATE_COL, edit: dt });
        out.push({ ...APPROVED_BY_COL, edit: combo });
      }
    }
    return out;
  }
  return cols.map((c) => {
    if (c.field === "validatedBy") return { ...c, edit: combo };
    if (c.field === "validatedDate") return { ...c, edit: dt };
    return c;
  });
}

export default function ResultValidationDetail({ batch, onClose, onDone, canWrite = true }: Props) {
  const [rows, setRows] = useState<SampleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [tab, setTab] = useState<"sample" | "qc">("sample");
  const [notesOpen, setNotesOpen] = useState(false);
  // True for the brief "✓ Approved"/"✓ Validated" flash between a successful
  // submit and the auto-return to the list (user request 2026-07-20) — without
  // this the action step's stepper state never had a moment to render 'done'
  // before the component unmounted.
  const [completed, setCompleted] = useState(false);
  // Employee roster for the ValidatedBy dropdown.
  const [empOptions, setEmpOptions] = useState<string[]>([]);
  // Per-row checkbox selection (ALPACA parity — the grid's first column).  Persists
  // ACROSS tabs so the guidance strip's "(N left)" review count spans Sample + QC
  // results.  Validate/Rollback still act on the whole batch.
  const [selRows, setSelRows] = useState<Set<string | number>>(new Set());
  // Rows whose ValidatedDate/ValidatedBy we PREVIEW-stamped on tick (so un-tick can
  // restore them, and a pre-validated row is never clobbered).  Cleared on reload.
  const [previewIds, setPreviewIds] = useState<Set<string>>(new Set());
  const notify = useNotify();
  const { confirm, prompt } = useConfirm();
  const notesCount = useNotesCount(batch.id, batch.jobOid ?? null);
  // Current user — the validator whose name previews in ValidatedBy on tick
  // (ALPACA client stamp: s.cpusername).  The DB write on Validate is authoritative.
  const meName = useAuthStore((s) => s.user?.name || s.user?.email || s.user?.id || "");

  // JobID mode loads the whole job's results (every QC batch) from the job
  // endpoint; QCBatchID mode loads one batch.  Mirrors ALPACA's two Retrieve
  // targets (DataReviewQueryPanelController.dataReviewNextAction).
  const isJob = batch.scope === "job";

  // ── Validation vs Approval (the ONE difference between the two Data-Review
  // stages) — endpoints, the editable stamp columns, action verb, audit form and
  // status wording all key off this.  Everything else is identical. ────────────
  const isAppr = batch.mode === "approval";
  const ENDPOINT = isAppr ? ENDPOINT_APPR : ENDPOINT_VAL;
  const stampByKey = (isAppr ? "approvedBy" : "validatedBy") as "approvedBy" | "validatedBy";
  const stampDateKey = (isAppr ? "approvedDate" : "validatedDate") as "approvedDate" | "validatedDate";
  // LIMS date-order guard on the editable stamp-date cell (DataReviewQueryPanel
  // BatchEditEndEditing): the new stamp must be >= its FLOOR date (AnalyzedDate for
  // Validation, ValidatedDate for Approval) and <= the current time — else the
  // matching LIMS message and the edit is rejected.
  const floorDateKey = (isAppr ? "validatedDate" : "analyzedDate") as "validatedDate" | "analyzedDate";
  const dateFloorMsg = isAppr
    ? "The approved date must be greater than the validated date."
    : "The validated date must be greater than the analyzed date.";
  const dateCurrentMsg = isAppr
    ? "The approved date must be less than the current date."
    : "The validated date must be less than the current date.";
  const actionVerb = isAppr ? "Approve" : "Validate"; // button + confirm
  const nextStage = isAppr ? "Pending Reporting" : "Pending Approval";
  const formName = isAppr ? "Result Approval" : "Result Validation";
  const pageId = isAppr ? "rappr" : "rval";
  const stampLabel = isAppr ? "ApprovedBy / Date" : "ValidatedBy / Date";

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      // Job scope = the whole job (all its batches).  Batch scope with a jobOid =
      // ONE job's samples in this batch + the batch's QC (per-(batch,job)); without
      // a jobOid = the whole batch.
      const url = isJob
        ? `${ENDPOINT}/job/${batch.id}/samples`
        : `${ENDPOINT}/${batch.id}/samples${batch.jobOid ? `?jobOid=${batch.jobOid}` : ""}`;
      const r = await api.get(url);
      setRows((r.data as { data: SampleRow[] }).data ?? []);
      // Fresh server state → drop any tick preview + selection.
      setSelRows(new Set());
      setPreviewIds(new Set());
    } catch {
      setRows([]);
      notify.error(`Failed to load ${isJob ? "job" : "batch"} results — check the API logs.`);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [batch.id, isJob, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  // Employee roster for the ValidatedBy dropdown — loaded once.
  useEffect(() => {
    let alive = true;
    api
      .get(EMP_OPTIONS_URL, { silentLoading: true } as never)
      .then((r) => {
        if (alive) setEmpOptions((r.data as { data: string[] }).data ?? []);
      })
      .catch(() => {
        /* dropdown just falls back to the current value */
      });
    return () => {
      alive = false;
    };
  }, []);

  // Browser Back closes the detail (parity with the SDMS package overlay).
  useEffect(() => {
    window.history.pushState({ rvDetail: true }, "");
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [onClose]);

  const sampleRows = useMemo(() => rows.filter((r) => !r.isQc), [rows]);
  // QC tab row order — LIMS parity (SampleParameter_ListView_Copy_QCResultApproval /
  // Result Approval + Validation): group by QCType ASCENDING, then SysSampleCode
  // DESCENDING within each type (e.g. CCV Low2 above CCV Low1, Method Blank2 above
  // Method Blank1). The backend SP returns SysSampleCode order (which scatters the
  // QC types); this refines the DISPLAY order for the QC tab only — Sample rows keep
  // the SP order. (2026-08-11, client "match LIMS row order".)
  const qcRows = useMemo(
    () =>
      rows
        .filter((r) => r.isQc)
        .sort((a, b) => {
          const byType = (a.qcType ?? "").localeCompare(b.qcType ?? "");
          if (byType !== 0) return byType;
          return (b.sys ?? "").localeCompare(a.sys ?? ""); // SysSampleCode DESC within type
        }),
    [rows],
  );
  const activeRows = tab === "sample" ? sampleRows : qcRows;
  // Results still un-ticked across BOTH tabs — drives the prototype's "(N left)"
  // guidance and the "Review complete" state.
  const reviewLeft = rows.reduce((n, r) => n + (selRows.has(r.id) ? 0 : 1), 0);
  // Per-tab completion for the stepper below — an empty tab (e.g. "QC Sample
  // Results (0)") counts as done with nothing to review, same as a tab whose
  // every row is ticked. Per StepIsland's own contract ("an OPTIONAL step
  // becomes 'done' only when its action actually happens, never on tab-open"),
  // this must be tick-based, not "has this tab merely been opened" — the prior
  // version marked `qc` 'todo' forever whenever it had 0 rows (nothing to ever
  // tick), which looked stuck/broken even after a successful Approve.
  const sampleDone = sampleRows.length === 0 || sampleRows.every((r) => selRows.has(r.id));
  const qcDone = qcRows.length === 0 || qcRows.every((r) => selRows.has(r.id));

  // Validate / Rollback act on QC-batch Oids.  Batch mode = this one batch; job
  // mode = every distinct batch the job's loaded rows belong to (the existing
  // endpoints already accept many batchIds).
  const targetBatchIds = useMemo<string[]>(
    () => (isJob ? Array.from(new Set(rows.map((r) => r.uqabid).filter((x): x is string => !!x))) : [batch.id]),
    [isJob, rows, batch.id],
  );

  const mode: "validation" | "approval" = isAppr ? "approval" : "validation";
  const sampleCols = useMemo(() => withStampEditors(COLS, empOptions, mode), [empOptions, mode]);
  const qcCols = useMemo(() => withStampEditors(qcColsForMode(mode), empOptions, mode), [empOptions, mode]);

  // Inline-edit commit — ValidatedBy (Employee display name) / ValidatedDate
  // (wire string).  PENDING-ONLY (user request 2026-07-10, ALPACA batch-edit
  // parity): the edit updates the local row buffer but is NOT persisted here and
  // shows NO "Saved successfully." toast.  Like LIMS's grid batch buffer, the
  // changes are committed to the DB only when the reviewer clicks Validate/Approve
  // (see doAction → /samples/save then /validate|/approve).  Back / Refresh discards.
  const onCellCommit = useCallback(
    (row: SampleRow, col: AppColumn<SampleRow>, value: string) => {
      const v = value === "" ? null : value;
      // LIMS BatchEditEndEditing date-order validation on the editable stamp-date
      // cell (ApprovedDate in approval mode, ValidatedDate in validation).  Runs
      // ONLY when BOTH the new stamp and its floor date exist (LIMS gates on both
      // non-null).  On failure show the LIMS message and REJECT the edit — the
      // inline editor has already closed, so the cell reverts to its stored value.
      if (col.field === stampDateKey && v) {
        const floor = row[floorDateKey];
        const t = new Date(v).getTime();
        if (!Number.isNaN(t)) {
          if (floor && t < new Date(floor).getTime()) {
            notify.error(dateFloorMsg);
            return;
          }
          if (t > Date.now()) {
            notify.error(dateCurrentMsg);
            return;
          }
        }
      }
      setRows((prev) => prev.map((r) => (r.id === row.id ? ({ ...r, [col.field]: v } as SampleRow) : r)));
      // A manual edit on the stamp employee/date cell means the reviewer has
      // deliberately taken ownership of this row's stamp — it's no longer just
      // the tick-to-preview auto-fill from onSelectionChange (browser-clock
      // based), so doAction must now send it to /samples/save even though the
      // untouched auto-preview must NOT be (see doAction — sending the
      // untouched preview persisted the VIEWER's browser clock instead of the
      // DB server's, a few minutes off — 2026-07-20).
      if (col.field === stampByKey || col.field === stampDateKey) {
        setPreviewIds((prev) => {
          if (!prev.has(row.id)) return prev;
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }
    },
    [stampDateKey, stampByKey, floorDateKey, dateFloorMsg, dateCurrentMsg, notify],
  );

  // Tick → auto-fill ValidatedDate / ValidatedBy (ALPACA DataReviewController's
  // client-side SelectionChanged: SetCellValue(ValidatedDate, today) +
  // SetCellValue(ValidatedBy, currentUser) on select, cleared on de-select).  This
  // is a PREVIEW only — like LIMS, the authoritative DB stamp happens on Validate
  // (AnalysisWriter stamps ValidatedBy=@UserOid, ValidatedDate=now WHERE
  // ValidatedBy IS NULL).  A row already validated by someone else is preserved
  // (only rows WE preview-stamped are cleared on un-tick).  Works on BOTH tabs —
  // the handler stamps by row id, and Sample/QC rows share `rows`.
  const onSelectionChange = useCallback(
    (next: Set<string | number>) => {
      const stamp = nowClientWire();
      const nextPreview = new Set(previewIds);
      setRows((prev) =>
        prev.map((r) => {
          const isSel = next.has(r.id);
          const wasSel = selRows.has(r.id);
          if (isSel === wasSel) return r;
          if (isSel) {
            // Stamp the stage's preview (Approved* in approval mode, Validated* in
            // validation mode) ONLY if that row isn't already stamped (preserve existing).
            if (!r[stampByKey] && !r[stampDateKey]) {
              nextPreview.add(r.id);
              return { ...r, [stampDateKey]: stamp, [stampByKey]: meName } as SampleRow;
            }
            return r;
          }
          // De-selected: clear only the cells WE preview-stamped.
          if (nextPreview.has(r.id)) {
            nextPreview.delete(r.id);
            return { ...r, [stampDateKey]: null, [stampByKey]: null } as SampleRow;
          }
          return r;
        }),
      );
      setPreviewIds(nextPreview);
      setSelRows(next);
    },
    [selRows, previewIds, meName, stampByKey, stampDateKey],
  );

  // Scope label for confirms / messages.  A jobOid means the action is job-scoped
  // (JOBID mode, or QCBatchID per-job) → "job 260623002"; only a bare whole-batch
  // action reads "QC batch QB…_076".
  const scopeLabel = isJob || batch.jobOid ? `job ${batch.jobID ?? ""}` : `QC batch ${batch.qcBatchID ?? ""}`;

  const doAction = useCallback(async () => {
    // ── Selection guards — ALPACA ResultValidation_Execute / ResultApproval_Execute
    // ── (ResultEntryViewController) via the shared §13 notifications.  Order matches
    // LIMS: the QC guard fires before the "nothing selected" info.
    //   • A ticked SAMPLE whose QC batch has an UN-ticked QC row → red error
    //     "Select the QC results from the QC result tab." (tick samples ⇒ must tick
    //     their QC too).  No symmetric sample guard (LIMS is asymmetric).
    //   • Nothing ticked in either tab → centered WARNING modal "Please select an
    //     item…" (§13: blocking guards are modals, not banners — 2026-08-11).
    const tickedSampleBatches = new Set<string>(
      sampleRows
        .filter((r) => selRows.has(r.id))
        .map((r) => r.uqabid)
        .filter((x): x is string => !!x),
    );
    const missingQc = qcRows.some((qc) => !!qc.uqabid && tickedSampleBatches.has(qc.uqabid) && !selRows.has(qc.id));
    if (missingQc) {
      notify.error("Select the QC results from the QC result tab.");
      return;
    }
    if (selRows.size === 0) {
      notify.warning("Please select an item to proceed further.");
      return;
    }

    // Result Validation AND Approval are JOB-scoped on the server (a non-batched job's
    // samples have no QC batch, so targetBatchIds can be empty) — proceed as long as we
    // have the job.
    if (targetBatchIds.length === 0 && !batch.jobOid) {
      notify.error(`Nothing pending to ${actionVerb.toLowerCase()}.`);
      return;
    }
    const ok = await confirm({
      title: `${actionVerb} Results`,
      message: `${actionVerb} ${scopeLabel}? Its pending results move to ${nextStage}.`,
      confirmLabel: actionVerb,
    });
    if (!ok) return;
    try {
      // Commit the pending stamp edits (Approved* / Validated*) for the ticked rows
      // (LIMS commits the grid batch buffer on submit — the edits were held locally,
      // NOT saved on each keystroke).  Silent — no per-edit toast.  The flip then
      // PRESERVES these (ISNULL) and stamps any still-null cells with the current
      // user + server-now.
      //
      // previewIds EXCLUDED here (fixed 2026-07-20): a ticked-but-never-manually-
      // edited row is still just onSelectionChange's tick-to-preview auto-fill —
      // a BROWSER-clock value (nowClientWire), not the DB server's. Sending it
      // here would persist that browser-clock stamp unconditionally, and the
      // flip's ISNULL(...,@StampedAt) below would then find the cell already
      // non-null and never reach its own (now DB-server-sourced) stamp. Leaving
      // an untouched preview row's stamp NULL here lets the flip fill it in
      // correctly instead. Only rows the reviewer actually edited via
      // onCellCommit (no longer in previewIds) are sent.
      const edits = rows
        .filter((r) => selRows.has(r.id) && !previewIds.has(r.id) && (r[stampDateKey] || r[stampByKey]))
        .map((r) =>
          isAppr
            ? { id: r.id, approvedBy: r.approvedBy, approvedDate: r.approvedDate }
            : { id: r.id, validatedBy: r.validatedBy, validatedDate: r.validatedDate },
        );
      if (edits.length > 0) {
        try {
          await api.post(`${ENDPOINT}/${batch.id}/samples/save`, { edits }, { silentLoading: true } as never);
        } catch {
          /* non-fatal — the flip still stamps server-now for un-saved rows */
        }
      }
      // jobOid scopes the flip to THIS job's samples (+ the batch's QC), so acting on
      // a per-(batch,job) view doesn't touch the batch's other jobs.  sampleParameterIds
      // = the ticked rows (samples + their QC) → PARTIAL: the server advances ONLY those
      // SampleParameter.Oids, matching ALPACA ResultEntryViewController (SelectedObjects
      // only), instead of every pending row in the job/batch.
      const res = await api.post(`${ENDPOINT}/${isAppr ? "approve" : "validate"}`, {
        batchIds: targetBatchIds,
        jobOid: batch.jobOid ?? null,
        sampleParameterIds: Array.from(selRows),
      });
      const d = res.data as { count: number; skipped?: Record<string, string> };
      if (d.count > 0) {
        // Flash the stepper's action step as done (✓ Approved/Validated), then the
        // MILESTONE success is the shared CENTERED modal (§18.6); on OK, return to
        // the list (replaces the old flash + auto-return top banner).
        setCompleted(true);
        await showDialog({
          type: "success",
          title: isAppr ? "Approved" : "Validated",
          message: `${isAppr ? "Approved" : "Validated"} successfully.`,
        });
        onDone();
      } else
        notify.error(
          Object.values(d.skipped ?? {})[0] ??
            `Nothing to ${actionVerb.toLowerCase()} in this ${isJob ? "job" : "batch"}.`,
        );
    } catch {
      notify.error(`${actionVerb} failed — check the API logs.`);
    }
  }, [
    rows,
    sampleRows,
    qcRows,
    selRows,
    previewIds,
    targetBatchIds,
    batch.id,
    batch.jobOid,
    scopeLabel,
    isJob,
    isAppr,
    ENDPOINT,
    actionVerb,
    nextStage,
    stampByKey,
    stampDateKey,
    confirm,
    notify,
    onDone,
  ]);

  const doRollback = useCallback(async () => {
    // Selection guard — ALPACA Rollback_Execute (ResultEntryViewController line
    // 2680-2683): nothing ticked in EITHER tab → centered WARNING modal "Please
    // select an item to proceed further." (the same `selectchkbox` message as
    // Validate; §13 blocking guard → modal, 2026-08-11). Rollback is symmetric —
    // no QC-specific guard, just "at least one item selected".
    if (selRows.size === 0) {
      notify.warning("Please select an item to proceed further.");
      return;
    }
    if (targetBatchIds.length === 0 && !(isAppr && batch.jobOid)) {
      notify.error("Nothing pending to roll back.");
      return;
    }
    try {
      const pre = await api.post(`${ENDPOINT}/rollback/precheck`, {
        batchIds: targetBatchIds,
        jobOid: batch.jobOid ?? null,
      });
      const blocked = (pre.data as { blocked?: Record<string, string> }).blocked ?? {};
      if (Object.keys(blocked).length > 0) {
        notify.error(Object.values(blocked)[0] ?? "Rollback failed.");
        return;
      }
    } catch {
      /* precheck hiccup shouldn't block; the write re-checks */
    }
    const reason = await prompt({
      title: "Result Rollback",
      message: "Enter a reason for the rollback.",
      placeholder: "Rollback reason…",
      required: true,
      confirmLabel: "Rollback",
    });
    if (reason === null) return;
    try {
      const res = await api.post(`${ENDPOINT}/rollback`, {
        batchIds: targetBatchIds,
        jobOid: batch.jobOid ?? null,
        reason,
        sampleParameterIds: Array.from(selRows),
      });
      const d = res.data as { count: number; skipped?: Record<string, string> };
      if (d.count > 0) {
        await showDialog({ type: "success", title: "Rollback", message: "Rollbacked successfully." });
        onDone();
      } else
        notify.error(Object.values(d.skipped ?? {})[0] ?? `Nothing to roll back in this ${isJob ? "job" : "batch"}.`);
    } catch {
      notify.error("Rollback failed — check the API logs.");
    }
  }, [selRows, targetBatchIds, batch.jobOid, isJob, isAppr, ENDPOINT, prompt, notify, onDone]);

  return (
    <div className="bl-page">
      {/* Result Validation workflow stepper (BO UI Standard §5) — docked in the
          top bar. Select Job is done (a batch was loaded); Sample Results / QC
          Sample Results go 'done' once every row in that tab is ticked (or the
          tab has 0 rows to review) — COMPLETION wins even while you're still
          viewing that tab (fixed 2026-07-20, user request: match the SDMS
          package detail's own stepper, which marks a step done as soon as it's
          reviewed, not just when you've tabbed away from it). Only an
          UNFINISHED tab you're currently on shows 'active'; tab-open alone
          never counts as done on its own (StepIsland's own contract). The
          final action step goes 'active' once nothing is left to tick
          (matching the "Review complete" banner below) and flips to 'done' for
          a brief moment after a successful submit (see `completed` /
          doAction) — a fixed 900ms window before doAction's
          setTimeout(onDone) unmounts this view. */}
      <TopbarStepper
        steps={
          [
            { id: "select", label: "Select Job", state: "done" },
            {
              id: "sample",
              label: "Sample Results",
              state: sampleDone ? "done" : tab === "sample" ? "active" : "todo",
            },
            { id: "qc", label: "QC Sample Results", state: qcDone ? "done" : tab === "qc" ? "active" : "todo" },
            { id: "action", label: actionVerb, state: completed ? "done" : reviewLeft === 0 ? "active" : "todo" },
          ] as IslandStep[]
        }
      />
      <div className="bl-top">
        {/* Breadcrumb navigation (user request 2026-07-10 — the detail was missing it;
            the LIST already shows Home › Data Review › Result Validation, and LIMS's
            detail carries a breadcrumb too).  "Result Validation" links back to the
            list; the trailing crumb is the job/batch being reviewed. */}
        <div className="dp-breadcrumb">
          <span>Home</span>
          <i className="ti ti-chevron-right" />
          <span>Data Review</span>
          <i className="ti ti-chevron-right" />
          <span
            className="dp-bc-link"
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onClose();
            }}
            title={`Back to the ${formName} list`}
          >
            {formName}
          </span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{batch.jobID ?? batch.qcBatchID ?? formName}</span>
        </div>
        <div className="dp-title-row">
          <div className="dp-titlewrap">
            <FormTitle
              pageId={pageId}
              title={formName}
              loadMs={loadMs}
              loading={loading}
              auditFormName={formName}
              auditSourceId={batch.id}
            />
          </div>
          {/* TOP-RIGHT actions (prototype parity): Back · Rollback · Validate/Approve
              — the terminal action is the orange solid primary. */}
          <div className="dp-title-actions">
            <button className="ca-btn" onClick={onClose} title={`Back to the ${formName} list`}>
              <i className="ti ti-arrow-left" /> Back
            </button>
            <button
              className="ca-btn"
              onClick={() => void doRollback()}
              disabled={completed || !canWrite}
              title={
                !canWrite
                  ? "Read-only access — contact your administrator to request Write permission"
                  : isAppr
                    ? "Roll this job back to Pending Validation"
                    : "Roll this batch back to Pending Entry"
              }
            >
              <i className="ti ti-arrow-back-up" style={{ color: "var(--ai-red)" }} /> Rollback
            </button>
            <button
              className="ca-btn ca-btn-solid"
              onClick={() => void doAction()}
              disabled={completed || !canWrite}
              title={
                !canWrite
                  ? "Read-only access — contact your administrator to request Write permission"
                  : `${actionVerb} this ${isJob || batch.jobOid ? "job" : "batch"}'s pending results`
              }
            >
              <i className="ti ti-circle-check" /> {completed ? `${actionVerb}d` : actionVerb}
            </button>
          </div>
        </div>
        {/* Identity line under the title (user request 2026-07-09; refer prototype):
            "{QCBatchID | JobID} · Client · ProjectName" — the batch/job being reviewed. */}
        {[batch.qcBatchID ?? batch.jobID, batch.client, batch.projectName].some(Boolean) && (
          <div className="dp-subtitle">
            {/* QCBatchID · JobID · Client · ProjectName — per-(batch,job) shows both
                the batch and the job being reviewed; JobID mode has no QCBatchID. */}
            {[batch.qcBatchID, batch.jobID, batch.client, batch.projectName].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>

      <div className="bl-grid-area">
        {/* ── Read-only MAC banner ──────────────────────────────────────── */}
        {!canWrite && (
          <div className="rval-readonly-banner">
            <i className="ti ti-eye" /> <b>Read-only access</b> — you can view results but cannot Rollback, Validate or Approve. Contact your administrator to request Write permission.
          </div>
        )}
        {/* Guidance strip (BO UI Standard §5, shared <StepHint>) — verbatim from the
            Data-Review prototype (user request 2026-07-09): the tick-to-review guidance
            with a live "(N left)" count, flipping to "Review complete" (green) when every
            Sample & QC result is ticked. */}
        {reviewLeft > 0 ? (
          <StepHint tone="guidance">
            <b>Review · {actionVerb}.</b> Review each result and <b>tick it</b> — ticking stamps <b>{stampLabel}</b>.
            Tick <b>every Sample Result and QC Sample Result</b> ({reviewLeft} left), then <b>{actionVerb}</b>. Each
            grid's header checkbox selects all.
          </StepHint>
        ) : (
          <StepHint tone="complete">
            <b>Review complete.</b> Every Sample &amp; QC result is ticked — click <b>{actionVerb}</b> to advance the
            job, or <b>Rollback</b>.
          </StepHint>
        )}
        {/* Result Approval — segregation-of-duties reminder (ALPACA
            ResultApproval_Execute: the approver must differ from the analyst).  Uses
            the SAME orange `guidance` tone as the review strip above / the Result
            Validation form (user request 2026-07-10: match Result Validation's colour). */}
        {isAppr && (
          // WORDING (2026-08-25): ANALYSIS, not "validation" — the gate compares the ANALYST
          // against the approver; a validator may approve what they validated. See the twin
          // banner in AnalysisListPage for the full note. The error DIALOG keeps ALPACA's
          // legacy wording for parity; these banners are ours alone.
          <StepHint tone="guidance">
            <i className="ti ti-info-circle" /> Analysis and approval of analysis results must be performed by{" "}
            <b>different users</b>.
          </StepHint>
        )}
        {/* Inline toolbar — Project Notes · Refresh. (Back · Rollback · Validate
            moved to the TOP-RIGHT header, prototype parity 2026-07-09; Save/Cancel
            changes removed — ValidatedBy/ValidatedDate edits auto-save on commit.) */}
        <div style={{ margin: "0 0 8px", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <button className="ca-btn" onClick={() => setNotesOpen(true)} title="Project notes for this batch">
            <i className="ti ti-notes" style={{ color: "var(--ai-icon-audit)" }} /> Project Notes ({notesCount})
          </button>
          <button className="ca-btn" onClick={() => void load()} title="Refresh">
            <i className="ti ti-refresh" /> Refresh
          </button>
        </div>

        {/* Sample Results / QC Results tabs — same tab bar as SDMS Result
            Validation (dv-tabs / dv-tab / dv-active). */}
        <div className="dv-tabs">
          <div className={`dv-tab ${tab === "sample" ? "dv-active" : ""}`} onClick={() => setTab("sample")}>
            Sample Results ({sampleRows.length})
          </div>
          <div className={`dv-tab ${tab === "qc" ? "dv-active" : ""}`} onClick={() => setTab("qc")}>
            QC Results ({qcRows.length})
          </div>
        </div>

        <div className="bl-card bl-card-flex" style={{ borderTopLeftRadius: 0 }}>
          <AppDataGrid<SampleRow>
            rows={activeRows}
            columns={tab === "sample" ? sampleCols : qcCols}
            getRowId={(r) => r.id}
            loading={loading}
            loadMs={loadMs}
            selectable
            selectedIds={selRows}
            onSelectionChange={onSelectionChange}
            stickyColumns={2}
            onCellCommit={onCellCommit}
            emptyText={tab === "sample" ? "No sample results in this batch." : "No QC results in this batch."}
          />
        </div>
      </div>

      {notesOpen && (
        <NotesListPopup
          batchId={batch.id}
          // Business Object + Name Source of a NEW note = this form's caption ("Result
          // Approval" / "Result Validation") — ALPACA sets NoteSource = NameSource =
          // View.Caption; the API tags the saved note the same via `source`.
          businessObject={formName}
          batchTest={formName}
          // Manual Result Validation scopes notes to the ONE displayed job (ALPACA
          // "Result Validation") — representative job in QCBatchID mode, selected job
          // in JobID mode — and titles the popup with the JobID (LIMS "Project Notes
          // - 260623002"), not the QC batch.
          jobOid={batch.jobOid ?? null}
          batchLabel={batch.jobID ?? batch.qcBatchID ?? ""}
          onClose={() => setNotesOpen(false)}
          onChanged={() => {
            /* count auto-refreshes via useNotesCount */
          }}
        />
      )}
    </div>
  );
}
